import { FabricCanvas, FabricGroup, FabricRect } from "../../objects";
import { LayerProps } from "./interfaces";
import { LayerCustomType } from "../../interfaces";
import { lockLayer, pickProps, setObjectPosition } from "../../utils";
import { ObjectId } from "../../helpers";
import Group from "../Group";

export default class Layer extends FabricGroup {
  protected static customType: LayerCustomType = LayerCustomType.Base;
  protected borderObject: FabricRect = new FabricRect({
    fill: "transparent",
    stroke: "black",
    strokeDashArray: [10, 10],
  });
  modifiedKeysSet: Set<string> = new Set();

  _id: string = new ObjectId().toString();
  functionalId: number = 1;
  name: string = "Layer";
  locked: boolean = false;
  removable: boolean = true;
  outlineVisible: boolean = true;
  groupObject?: Group;
  x: number = 0;
  y: number = 0;
  updated: boolean = false;

  get customType(): LayerCustomType {
    return (this.constructor as typeof Layer).customType;
  }

  set customType(type: LayerCustomType) {}

  constructor(options: Partial<LayerProps> = {}) {
    super();

    this.add(this.borderObject);
    this.set(options);

    this.on("added", (event) => {
      const { locked, x, y, left, top } = this;
      lockLayer(this, locked);
      if (event.target instanceof FabricCanvas) {
        this.setPosition({ x: x || left, y: y || top });
        this.on("modified", () => {
          this.updated = true;
        });
      }
      this.rerender.bind(this)();
    });
    this.on("modified", this.rerender.bind(this));
  }

  protected updateBorder() {
    const strokeWidth = this.borderObject.strokeWidth;
    const width = this.width - strokeWidth;
    const height = this.height - strokeWidth;
    this.borderObject
      .set({
        width: width,
        height: height,
        left: -(width + strokeWidth) / 2,
        top: -(height + strokeWidth) / 2,
        visible: this.outlineVisible,
      })
      .scale(1);
  }

  protected rerender() {
    if (!this.canvas) return;

    if (!this.group) {
      const { originX, originY } = this.canvas.layerOrigin;
      const { x, y } = this.getPointByOrigin(originX, originY);
      this.set({ x, y });
    }

    const { width, height, scaleX, scaleY } = this;
    this.set({ width: width * scaleX, height: height * scaleY }).scale(1);
    this.updateBorder();

    this.canvas.requestRenderAll();
  }

  getDefaultDimensions(size: number): { width: number; height: number } {
    return { width: size / 3, height: size / 3 };
  }

  async clone(): Promise<this> {
    const clonedLayerObject = this.toObject();
    delete clonedLayerObject._id;
    delete clonedLayerObject.customType;
    const clonedObject = new (this.constructor as typeof Layer)(
      clonedLayerObject,
    );
    return clonedObject as this;
  }

  toObject() {
    const baseProps: any = pickProps(this, [
      "_id",
      "functionalId",
      "name",
      "left",
      "top",
      "width",
      "height",
      "scaleX",
      "scaleY",
      "angle",
      "visible",
      "locked",
      "removable",
      "x",
      "y",
      "updated",
    ]);

    const transformSettings = pickProps(this, [
      "left",
      "top",
      "width",
      "height",
      "scaleX",
      "scaleY",
      "angle",
      "flipX",
      "flipY",
    ]);

    return {
      ...baseProps,
      customType: (this.constructor as typeof Layer).customType,
      groupId: this.groupObject?._id ?? null,
      transformSettings,
      modifiedSettingKeys: [...this.modifiedKeysSet],
    };
  }

  setPosition(key: "x" | "y" | { x: number; y: number }, value: number = 0) {
    setObjectPosition(this, key, value);
    this.rerender();
  }

  modify(key: string) {
    this.modifiedKeysSet.add(key);
  }
}

export * from "./interfaces";
